import React, { FC, useEffect, useRef, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { CustomUploadAdapterPlugin } from 'src/utils/CustomUploadAdapterPlugin';
import ClassicEditor, { customEditorPlugins } from 'src/utils/CustomClassicEditor';
import {
  isFullHtmlDocument,
  looksLikeHtmlSource,
  mergeEditorBodyIntoHtmlDocument,
  prepareHtmlDocumentForEditor,
} from 'src/utils/emailHtmlDocument';
import { prepareDescriptionValueForSave as prepareDescriptionForSave } from 'src/utils/descriptionHtmlSource';

export const prepareDescriptionValueForSave = prepareDescriptionForSave;


type AppCustomEditorPropsType = {
  value: string;
  onChangeData: (params: string) => void;
  placeholder?: string;
  maxCharacters?: number;
  onLimitExceed?: (params: { currentLength: number; limit: number }) => void;
  handleHtmlPaste?: boolean;
  onHtmlDocumentPaste?: (html: string) => void;
};

const getPlainTextLength = (html: string): number => {
  return html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim().length;
};

const AppCustomEditor: FC<AppCustomEditorPropsType> = ({
  value,
  placeholder,
  onChangeData,
  maxCharacters,
  onLimitExceed,
  handleHtmlPaste = false,
  onHtmlDocumentPaste,
}) => {
  const [editorData, setEditorData] = useState<string>(() =>
    handleHtmlPaste ? prepareHtmlDocumentForEditor(value) : value
  );
  const previousValidValueRef = useRef<string>(value);
  const isRestoringRef = useRef<boolean>(false);
  const isApplyingHtmlPasteRef = useRef<boolean>(false);
  const isApplyingExternalValueRef = useRef<boolean>(false);
  const pastedEditorValueRef = useRef<string | null>(null);
  const editorBaselineRef = useRef<string>('');
  const lastEmittedValueRef = useRef<string | null>(null);
  const fullHtmlDocumentRef = useRef<string | null>(
    handleHtmlPaste && isFullHtmlDocument(value) ? value : null
  );
  const onHtmlDocumentPasteRef = useRef(onHtmlDocumentPaste);
  onHtmlDocumentPasteRef.current = onHtmlDocumentPaste;

  useEffect(() => {
    if (lastEmittedValueRef.current === value) {
      lastEmittedValueRef.current = null;
      return;
    }

    previousValidValueRef.current = value;
    fullHtmlDocumentRef.current =
      handleHtmlPaste && isFullHtmlDocument(value) ? value : null;

    const nextEditorData = handleHtmlPaste ? prepareHtmlDocumentForEditor(value) : value;
    setEditorData((currentEditorData) => {
      if (currentEditorData === nextEditorData) return currentEditorData;

      isApplyingExternalValueRef.current = true;
      return nextEditorData;
    });
  }, [handleHtmlPaste, value]);

  return (
      <CKEditor
        editor={ClassicEditor}
        data={editorData}
        config={{
          plugins: customEditorPlugins,
          placeholder: placeholder,
          ...(handleHtmlPaste
            ? {
                htmlSupport: {
                  allow: [
                    {
                      name: /.*/,
                      attributes: true,
                      classes: true,
                      styles: true,
                    },
                  ],
                },
              }
            : {}),
          // plugins:[ Image],
          image: {
            toolbar: ['imageTextAlternative', 'imageStyle:full', 'imageStyle:side', 'resizeImage'],
            resizeUnit: '%',
            resizeOptions: [
              {
                name: 'resizeImage:original',
                value: null,
                icon: 'original',
              },
              {
                name: 'resizeImage:50',
                value: '50',
                icon: 'medium',
              },
              {
                name: 'resizeImage:75',
                value: '75',
                icon: 'large',
              },
            ],
          },
          toolbar: [
            'heading',
            '|',
            'bold',
            'italic',
            'link',
            'bulletedList',
            'numberedList',
            'blockQuote',
            'undo',
            'redo',
            'imageUpload',
          ],
          extraPlugins: [CustomUploadAdapterPlugin],
          ui: {
            poweredBy: {
              label: 'Commurcium',
              horizontalOffset: 0,
              position: 'border',
              side: 'right',
              verticalOffset: 0,
              forceVisible: false,
            },
          },
        }}
        onReady={(editor) => {
          editor.editing.view.document.on(
            'clipboardInput',
            (event, data: any) => {
              const handleDocumentPaste = onHtmlDocumentPasteRef.current
              if (!handleDocumentPaste || data.method !== 'paste') return

              const pastedText = data.dataTransfer.getData('text/plain')
              if (!looksLikeHtmlSource(pastedText)) return

              event.stop()
              handleDocumentPaste(pastedText)
            },
            { priority: 'highest' }
          )

          if (handleHtmlPaste) {
            editor.editing.view.document.on(
              'clipboardInput',
              (event, data: any) => {
                if (data.method !== 'paste') return;

                const pastedText = data.dataTransfer.getData('text/plain');
                if (!looksLikeHtmlSource(pastedText)) return;

                event.stop();
                fullHtmlDocumentRef.current = isFullHtmlDocument(pastedText)
                  ? pastedText
                  : null;
                previousValidValueRef.current = pastedText;
                isApplyingHtmlPasteRef.current = true;
                editor.setData(prepareHtmlDocumentForEditor(pastedText));
                isApplyingHtmlPasteRef.current = false;
                pastedEditorValueRef.current = editor.getData();
                editorBaselineRef.current = pastedEditorValueRef.current;
                lastEmittedValueRef.current = pastedText;
                onChangeData(pastedText);
              },
              { priority: 'high' }
            );
          }

          editorBaselineRef.current = editor.getData();

          console.log('Editor is ready to use!', editor);
        }}
        onChange={(event, editor) => {
          if (isApplyingHtmlPasteRef.current) {
            isApplyingHtmlPasteRef.current = false;
            return;
          }

          if (isRestoringRef.current) {
            isRestoringRef.current = false;
            return;
          }

          if (isApplyingExternalValueRef.current) {
            isApplyingExternalValueRef.current = false;
            editorBaselineRef.current = editor.getData();
            return;
          }

          const newValue = editor.getData();

          if (pastedEditorValueRef.current === newValue) {
            return;
          }

          pastedEditorValueRef.current = null;

          if (maxCharacters) {
            const currentLength = getPlainTextLength(newValue);
            if (currentLength > maxCharacters) {
              isRestoringRef.current = true;
              editor.setData(
                handleHtmlPaste
                  ? prepareHtmlDocumentForEditor(previousValidValueRef.current)
                  : previousValidValueRef.current
              );
              onLimitExceed?.({ currentLength, limit: maxCharacters });
              return;
            }
          }

          const outputValue = fullHtmlDocumentRef.current
            ? mergeEditorBodyIntoHtmlDocument(
                fullHtmlDocumentRef.current,
                editorBaselineRef.current,
                newValue
              )
            : newValue;

          editorBaselineRef.current = newValue;
          fullHtmlDocumentRef.current = isFullHtmlDocument(outputValue) ? outputValue : null;
          previousValidValueRef.current = outputValue;
          lastEmittedValueRef.current = outputValue;
          onChangeData(outputValue);
        }}
        onBlur={(event, editor) => {
          console.log('Blur.', editor);
        }}
        onFocus={(event, editor) => {
          console.log('Focus.', editor);
        }}
      />
  );
};

export default AppCustomEditor;
